// src/stores/expenseStore.js
import { defineStore } from "pinia";
import { ref } from "vue";
import service from "@/services/finance/expense.service";
import * as Toast from "@/utils/toast";
import { createScopedCache } from "./_cache";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

const expenseRangeStatsCache = createScopedCache("finance:expense:stats-range");
const EXPENSE_RANGE_STATS_TTL = 2 * 60 * 1000;

const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

export const useExpenseStore = defineStore("expense", () => {
  const items = ref([]);
  const byId = ref(Object.create(null));
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });
  const loading = ref(false);
  const error = ref(null);

  const stats = ref({
    total: 0,
    pending: 0,
    approved: 0,
    paid: 0,
    count: 0,
  });

  const filters = ref({
    q: "",
    category: "",
    payment_method: "",
    status: "",
    date_from: "",
    date_to: "",
    min_amount: null,
    max_amount: null,
    per_page: 25,
    page: 1,
  });

  function normalize(raw = {}) {
    return {
      id: raw.id,
      name: raw.name || "-",
      expense_date: raw.expense_date || null,
      amount: Number(raw.amount ?? 0),
      currency: raw.currency || "KES",
      category: raw.category || "",
      payment_method: raw.payment_method || "",
      reference: raw.reference || "",
      payee: raw.payee || "",
      status: raw.status || "Pending",
      notes: raw.notes || "",
      created_at: raw.created_at || null,
      updated_at: raw.updated_at || null,
    };
  }

  function upsertOne(raw) {
    const n = normalize(raw);
    byId.value[n.id] = n;
    const idx = items.value.findIndex((x) => x.id === n.id);
    if (idx === -1) items.value.unshift(n);
    else items.value.splice(idx, 1, n);
    return n;
  }

  async function list(params = {}) {
    return await fetchAll(params);
  }

  async function fetchAll(extra = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.FA_SALES))) {
      items.value = [];
      byId.value = Object.create(null);
      pagination.value = { total: 0, per_page: 25, current_page: 1 };
      return { data: { data: [], meta: pagination.value } };
    }

    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      const { data } = await service.list(params);

      if (data && data.data && data.meta) {
        const list = data.data.map(normalize);
        items.value = list;
        byId.value = Object.fromEntries(list.map((x) => [x.id, x]));
        pagination.value = {
          total: data.meta.total,
          per_page: data.meta.per_page,
          current_page: data.meta.current_page,
        };
      } else {
        const list = (data.data ?? data ?? []).map(normalize);
        items.value = list;
        byId.value = Object.fromEntries(list.map((x) => [x.id, x]));
        pagination.value = {
          total: data.total ?? items.value.length,
          per_page: data.per_page ?? params.per_page ?? 25,
          current_page: data.current_page ?? params.page ?? 1,
        };
      }

      return {
        data: {
          data: items.value,
          meta: pagination.value,
        },
      };
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch expenses";
      notifyError(error.value);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchStats(extra = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.FA_SALES))) {
      return;
    }

    try {
      const params = { ...filters.value, ...extra };
      const { data } = await service.stats(params);
      stats.value = {
        total: Number(data.total ?? 0),
        pending: Number(data.pending ?? 0),
        approved: Number(data.approved ?? 0),
        paid: Number(data.paid ?? 0),
        count: Number(data.count ?? 0),
      };
    } catch (e) {
      // Don't block UI, just log
      console.error("Failed to fetch expense stats", e);
    }
  }

  async function create(payload) {
    loading.value = true;
    error.value = null;
    try {
      await service.create(payload);
      notifySuccess("Expense created");
      await Promise.all([fetchAll(), fetchStats()]);
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to create expense";
      error.value = msg;
      notifyError(msg);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    error.value = null;
    try {
      await service.update(id, payload);
      notifySuccess("Expense updated");
      await Promise.all([fetchAll(), fetchStats()]);
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update expense";
      error.value = msg;
      notifyError(msg);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    error.value = null;
    try {
      await service.remove(id);
      notifySuccess("Expense deleted");
      await Promise.all([fetchAll(), fetchStats()]);
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete expense";
      error.value = msg;
      notifyError(msg);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchOne(id) {
    if (!id) return null;
    loading.value = true;
    error.value = null;
    try {
      const { data } = await service.get(id);
      return upsertOne(data);
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch expense";
      notifyError(error.value);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function get(id) {
    return await fetchOne(id);
  }

  async function show(id) {
    return await fetchOne(id);
  }

  async function find(id) {
    return await fetchOne(id);
  }

  async function statsForRange(extra = {}) {
    try {
      return await expenseRangeStatsCache.get(extra, {
        ttlMs: EXPENSE_RANGE_STATS_TTL,
        force: extra.force === true,
        loader: async () => {
          const params = { ...filters.value, ...extra };
          const { data } = await service.stats(params);
          return {
            total: Number(data.total ?? 0),
            pending: Number(data.pending ?? 0),
            approved: Number(data.approved ?? 0),
            paid: Number(data.paid ?? 0),
            count: Number(data.count ?? 0),
          };
        },
      });
    } catch (e) {
      console.error("Failed to fetch expense stats for range", e);
      return {
        total: 0,
        pending: 0,
        approved: 0,
        paid: 0,
        count: 0,
      };
    }
  }

  async function ensureStatsForRange(extra = {}) {
    return statsForRange({ ...extra, force: false });
  }

  async function exportPdf(extra = {}) {
    // we can optionally show loader
    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      const res = await service.exportPdf(params);
      return res.data; // blob
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to export expenses PDF";
      error.value = msg;
      notifyError(msg);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items,
    list,
    byId,
    pagination,
    loading,
    error,
    filters,
    stats,
    fetchAll,
    fetchStats,
    fetchOne,
    get,
    show,
    find,
    create,
    update,
    remove,
    statsForRange,
    ensureStatsForRange,
    exportPdf,
  };
});
